// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { TranslateService } from '@ngx-translate/core';
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionsService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private translateService: TranslateService,
  ) { }

  /**
   * Create a new subscription
   *
   * @since 1.0.0
   *
   * @param subscription Subscription data
   * @returns Observable<any>
   */
  create(subscription: any): Observable<any> {
    return this.apiService.apiCall('POST', 'subscriptions', subscription);
  }

  /**
   * Get subscriptions list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'subscriptions', params);
  }

  /**
   * Get an exportation file of all subscriptions
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'subscriptions/export', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given subscriptionId data from back-end
   *
   * @since 1.0.0
   *
   * @param subscriptionId ID of the subscription to get
   */
  get(subscriptionId: string): Observable<any> {
    return this.apiService.apiCall('GET', `subscriptions/${subscriptionId}`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get given subscriptionKey data from back-end
   *
   * @since 1.0.0
   *
   * @param subscriptionKey ID of the subscription to get
   */
  getByKey(subscriptionKey: string): Observable<any> {
    return this.apiService.apiCall('GET', `subscriptions`, { subscriptionKey }).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update subscription with given subscriptionId with given subscription data
   *
   * @since 1.0.0
   *
   * @param subscriptionId ID of subscription to update
   * @param data New subscription's data
   */
  update(subscriptionId: string, data: object): Observable<any> {
    return this.apiService.apiCall('PUT', `subscriptions/${subscriptionId}`, data);
  }

  /**
   * Delete permanently a subscription
   *
   * @since 1.0.0
   *
   * @param subscriptionId ID of the subscription
   */
  delete(subscriptionId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `subscriptions/${subscriptionId}`);
  }
}
